import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Modal, Table } from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { captureException } from 'utils/errors';
import { getData } from 'utils';
import { deleteFlow, getFlows } from 'api/flows';
import RunFlowModal from './RunFlowModal';

const pageSize = 10;

function Flows() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const navigate = useNavigate();

  const loadFlows = useCallback(() => {
    setLoading(true);
    getFlows({ page, page_size: pageSize })
      .then((res) => {
        const data = getData(res)?.data ?? getData(res);
        const results = data?.results ?? [];
        setFlows(Array.isArray(results) ? results : []);
        setPagination({
          count: data?.count ?? 0,
          next: data?.next,
          previous: data?.previous,
        });
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);
      });
  }, [page]);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const handleCreate = () => {
    navigate('/console/marketing/flows/create');
  };

  const handleEdit = (flow) => {
    navigate(`/console/marketing/flows/edit/${flow.id}`);
  };

  const handleDuplicate = (flow) => {
    navigate(`/console/marketing/flows/create`, { state: { duplicateFromId: flow.id } });
  };

  const handleDelete = (flow) => {
    Modal.confirm({
      title: 'Delete Flow',
      content: `Are you sure you want to delete "${flow.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () =>
        deleteFlow(flow.id)
          .then(() => loadFlows())
          .catch((e) => {
            captureException(e);
            throw e;
          }),
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleEdit(row);
          }}
        >
          {name}
        </a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val) => (
        <span className={val === 'active' ? 'status-active' : 'status-inactive'}>
          {val === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val) => (val ? new Date(val).toLocaleDateString() : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <>
          <Button type="link" size="small" onClick={() => handleEdit(row)}>
            Edit
          </Button>
          <Button type="link" size="small" onClick={() => handleDuplicate(row)}>
            Duplicate
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(row)}>
            Delete
          </Button>
        </>
      ),
    },
  ];

  const isEmpty = !loading && pagination.count === 0;

  return (
    <Layout
      header={(
        <Layout.Header
          title="Flows"
          extra={[
            <Button
              key="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Create Flow
            </Button>,
          ]}
        />
      )}
    >
      {isEmpty ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 48 }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No flows yet</p>
            <p style={{ color: '#666', marginBottom: 24 }}>
              Create flows to automate webhooks triggered from customer or company profiles.
            </p>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Create Flow
            </Button>
          </div>
        </Card>
      ) : (
        <Table
          loading={loading}
          columns={columns}
          dataSource={flows}
          rowKey="id"
          pagination={{
            current: page,
            pageSize,
            total: pagination.count,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      )}
    </Layout>
  );
}

export default withGuard(Flows, [PERMISSIONS.MARKETING], { redirect: true });
export { RunFlowModal };
